import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { UniversalHeader } from "@/components/layout/UniversalHeader/UniversalHeader";
import { UniversalFooter } from "@/components/layout/UniversalFooter/UniversalFooter";
import { TopBar } from "@/components/layout/TopBar/TopBar";
import { LeftNav } from "@/components/layout/LeftNav/LeftNav";
import { RightNav } from "@/components/layout/RightNav/RightNav";
import { PartnerGuard } from "@/components/shared/PartnerGuard/PartnerGuard";
import { useSupportedLocale } from "@/hooks/useSupportedLocale";
import { useCurrentPage } from "@/hooks/useCurrentPage";
import { useTranslations } from "@/hooks/useTranslations";
import { getLocalePreference } from "@/utils/localeStorageHelpers";
import { normalizeLocaleForCulture } from "@/utils/localeRouteHelpers";
import styles from "./Layout.module.scss";

interface ISectionItem {
  id: string;
  title: string;
}


export function Layout(): ReactNode {
  const [sections, setSections] = useState<ISectionItem[]>([]);
  const [rightNavTitleState, setRightNavTitleState] = useState<{
    path: string;
    title: string | undefined;
  }>({ path: "", title: undefined });
  const location = useLocation();
  const params = useParams();

  const rightNavTitle =
    rightNavTitleState.path === location.pathname ? rightNavTitleState.title : undefined;
  const setRightNavTitle = useCallback(
    (title: string | undefined): void => {
      setRightNavTitleState((previous) => {
        if (previous.path === location.pathname && previous.title === title) {
          return previous;
        }

        return { path: location.pathname, title };
      });
    },
    [location.pathname]
  );

  const outletContext = useMemo(
    () => ({ setSections, setRightNavTitle }),
    [setSections, setRightNavTitle]
  );

  const isChangeLocaleRoute = location.pathname.endsWith("/change-locale");
  const { isLocaleValid, isLoading: isLocaleCheckLoading } = useSupportedLocale(params.locale);
  const storedLocaleCode = normalizeLocaleForCulture(getLocalePreference()?.localeCode);
  const showNav = isLocaleValid || isLocaleCheckLoading;
  const { isMobileNavOpen, setIsMobileNavOpen } = useCurrentPage();
  const { t } = useTranslations();

  useEffect(() => {
    if (!isChangeLocaleRoute) {
      sessionStorage.setItem("previousRoute", location.pathname);
    }
  }, [location.pathname, isChangeLocaleRoute]);

  useEffect(() => {
    const routeLocale = normalizeLocaleForCulture(params.locale);
    const htmlLang = routeLocale ?? storedLocaleCode ?? "en-US";

    if (document.documentElement.lang !== htmlLang) {
      document.documentElement.lang = htmlLang;
    }
  }, [params.locale, storedLocaleCode]);

  return (
    <>
      <a className={styles.skipLink} href="#main-content">
        {t("navigation.skipToMainContent")}
      </a>
      <UniversalHeader />
      <TopBar />
      <div className={styles.pageContainer}>
        {isChangeLocaleRoute ? (
          <main id="main-content" className={styles.outletContainer} tabIndex={-1}>
            <Outlet context={outletContext} />
          </main>
        ) : (
          <PartnerGuard>
            {showNav ? (
              <>
                {isMobileNavOpen && (
                  <div
                    className={styles.mobileNavOverlay}
                    onClick={(): void => setIsMobileNavOpen(false)}
                    aria-hidden="true"
                  />
                )}
                <LeftNav />
              </>
            ) : null}
            <main id="main-content" className={styles.outletContainer} tabIndex={-1}>
              <Outlet context={outletContext} />
            </main>
            {showNav ? <RightNav sections={sections} title={rightNavTitle} /> : null}
          </PartnerGuard>
        )}
      </div>
      <UniversalFooter />
    </>
  );
}
